import type { LeadStatusChangeRequestT } from '@utazzo/shared';

type ReviewerActor = { id: string; role: string } | null | undefined;

/**
 * Active pending approval cue for a lead detail header.
 *
 * Mirrors LeadStatusChanger's stale filter: a STATUS_CHANGE whose `from`
 * no longer matches the live status is treated as gone (defense-in-depth
 * against stale cache). LOST / WON / DESTINATION_CHANGE stay visible.
 *
 * When this returns null (approve / reject / cancel cleared the request),
 * header Edit / status / New version unlock automatically — no extra flag.
 */
export function resolvePendingLeadApproval<
  T extends {
    kind: string;
    fromStatus?: { id: string } | null;
  },
>(pending: T | null | undefined, currentStatusId: string): T | null {
  if (!pending) return null;
  if (
    pending.kind === 'STATUS_CHANGE' &&
    pending.fromStatus &&
    pending.fromStatus.id !== currentStatusId
  ) {
    return null;
  }
  return pending;
}

/**
 * Mirrors the server's ADMIN-only direct-transition predicate (2026-08-06,
 * SALES_LEAD approval parity). Drives the copy on every surface that can
 * close a lead as Lost / Won or reopen it - the Status Changer and the
 * Follow-up outcome forms - so the operator is told upfront whether saving
 * acts directly or raises an approval request. Server is the source of
 * truth; this only picks the wording.
 *
 * SALES_LEAD is now treated exactly like SALES_AGENT for this decision -
 * only ADMIN acts on the pipeline directly. This reverses the 2026-07-14 /
 * 2026-07-25 SALES_LEAD-direct behavior.
 */
export function canCloseLeadAsLostDirectly(me: ReviewerActor): boolean {
  return me?.role === 'ADMIN';
}

/**
 * Mirrors server-side `assertCanReviewStatusChangeRequest` for UI gating.
 *
 * 2026-07-14: widened to let admins co-review ANY pending request
 * (assigned or not, any kind including DESTINATION_CHANGE) so the
 * approve/reject buttons appear on the header banner and matter in
 * the inbox. Row-creation routing is unchanged; admins are a fallback
 * on top of the assigned SALES_LEAD, not a replacement.
 */
export function canReviewLeadApprovalRequest(
  request: Pick<LeadStatusChangeRequestT, 'kind' | 'assignedReviewer'>,
  me: ReviewerActor,
): boolean {
  if (!me) return false;
  if (me.role === 'ADMIN') return true;
  if (me.role === 'SALES_LEAD' && request.assignedReviewer?.id === me.id) return true;
  return false;
}
